import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { ReactComponent as Afrodineicon } from '../afrodine_logo.svg';
import namkeLogoHeader from '../assets/namke-logo-header.png';

const DG = 'rgb(5, 70, 37)';

/**
 * @param {{ brand?: 'afrodine' | 'namke' }} props
 */
export function Footer({ brand = 'namke' }) {
  const { t } = useTranslation();
  const isNamke = brand !== 'afrodine';
  const hoverClass = isNamke
    ? 'hover:text-[rgb(239,76,35)]'
    : 'hover:text-green-600';

  if (!isNamke) {
    return (
      <footer className="flex flex-col md:flex-row text-lg p-12 sm:p-16 items-center justify-center gap-x-8 md:gap-x-16 lg:gap-x-24 text-zinc-800 bg-zinc-100">
        <div className="text-center md:text-left">
          <div className="flex flex-col items-center md:items-start mb-4">
            <Afrodineicon className="h-12 w-auto mb-2" />
            <h3 className="font-bold text-xl text-zinc-800">namke</h3>
          </div>
          <p className="text-sm font-normal mb-2 text-gray-900">afrodine.de</p>
          <p className="text-sm font-normal text-gray-900 mb-2">{t('footer.address')}</p>
        </div>
        <div className="text-center md:text-left">
          <p className="font-bold mb-4 text-zinc-800">{t('footer.usefulLinks')}</p>
          <Link to="/about-us" className={`text-sm font-normal text-gray-900 mb-2 transition-colors cursor-pointer block ${hoverClass}`}>{t('footer.aboutUs')}</Link>
          <Link to="/terms-of-service" className={`text-sm font-normal text-gray-900 mb-2 transition-colors cursor-pointer block ${hoverClass}`}>{t('footer.termsOfService')}</Link>
          <Link to="/privacy-policy" className={`text-sm font-normal text-gray-900 mb-2 transition-colors cursor-pointer block ${hoverClass}`}>{t('footer.privacyPolicy')}</Link>
          <Link to="/impressum" className={`text-sm font-normal text-gray-900 mb-2 transition-colors cursor-pointer block ${hoverClass}`}>{t('footer.imprint')}</Link>
          <Link to="/contact" className={`text-sm font-normal text-gray-900 transition-colors cursor-pointer block ${hoverClass}`}>{t('footer.contact')}</Link>
        </div>
        <div className="text-center md:text-left">
          <p className="font-bold mb-4 text-zinc-800">{t('footer.socialMedia')}</p>
          <div className="flex flex-col gap-2">
            <a href="https://instagram.com/namke_app" target="_blank" rel="noopener noreferrer" className={`text-sm font-normal text-gray-900 transition-colors flex items-center justify-center md:justify-start gap-2 ${hoverClass}`}>
              <i className="fab fa-instagram" />{t('footer.instagramHandle')}
            </a>
            <Link to="/contact" className={`text-sm font-normal text-gray-900 transition-colors flex items-center justify-center md:justify-start gap-2 ${hoverClass}`}>
              <i className="fas fa-envelope" />{t('footer.contactUs')}
            </Link>
          </div>
        </div>
        <div className="text-center md:text-left mt-8 md:mt-0">
          <p className="text-sm font-normal text-gray-900">{t('footer.copyright', { year: new Date().getFullYear(), brand: 'Afrodine' })}</p>
        </div>
      </footer>
    );
  }

  const labelStyle = { fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#EF4C23', margin: '0 0 9px' };
  const linkStyle = { color: 'rgba(5,16,4,0.78)', textDecoration: 'none' };

  return (
    <footer style={{ background: '#FBF9D9', padding: 'clamp(40px, 5vw, 72px) 0 32px', fontFamily: "'Poppins', sans-serif", color: '#051004' }}>
      <div style={{ maxWidth: '1320px', margin: '0 auto', padding: '0 clamp(16px, 3vw, 40px)', display: 'flex', flexDirection: 'column', gap: '34px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))', gap: '28px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <img src={namkeLogoHeader} alt="namke" style={{ height: '34px', width: 'auto', objectFit: 'contain' }} />
            <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontStyle: 'italic', fontSize: '16px', color: 'rgba(5,16,4,0.74)' }}>{t('footer.tagline')}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', fontFamily: 'Archivo, sans-serif', fontSize: '14.5px' }}>
            <span style={labelStyle}>{t('footer.discover')}</span>
            <Link to="/feed" style={{ ...linkStyle, marginBottom: '9px' }} className={hoverClass}>{t('footer.dishesOfTheDay')}</Link>
            <Link to="/feed" style={{ ...linkStyle, marginBottom: '9px' }} className={hoverClass}>{t('footer.restaurants')}</Link>
            <Link to="/traiteur" style={linkStyle} className={hoverClass}>{t('footer.cateringService')}</Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', fontFamily: 'Archivo, sans-serif', fontSize: '14.5px' }}>
            <span style={labelStyle}>{t('footer.cook')}</span>
            <Link to="/business/signup" style={{ ...linkStyle, marginBottom: '9px' }} className={hoverClass}>{t('footer.becomeACook')}</Link>
            <Link to="/business/login" style={{ ...linkStyle, marginBottom: '9px' }} className={hoverClass}>{t('footer.proSpace')}</Link>
            <Link to="/about-us" style={linkStyle} className={hoverClass}>{t('footer.aboutUs')}</Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', fontFamily: 'Archivo, sans-serif', fontSize: '14.5px', color: 'rgba(5,16,4,0.78)' }}>
            <span style={labelStyle}>Berlin</span>
            <a href="https://instagram.com/namke_app" target="_blank" rel="noopener noreferrer" style={linkStyle} className={hoverClass}>
              <i className="fab fa-instagram" style={{ marginRight: '6px' }} />{t('footer.instagramHandle')}
            </a>
            <Link to="/contact" style={linkStyle} className={hoverClass}>{t('footer.contactUs')}</Link>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', justifyContent: 'space-between', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.6)', borderTop: '1px solid rgba(5,16,4,0.12)', paddingTop: '22px' }}>
          <span>{t('footer.copyright', { year: new Date().getFullYear(), brand: 'namke' })}</span>
          <span style={{ display: 'flex', gap: '14px' }}>
            <Link to="/terms-of-service" style={{ color: 'inherit', textDecoration: 'none' }} className={hoverClass}>{t('footer.termsOfService')}</Link>
            <Link to="/privacy-policy" style={{ color: 'inherit', textDecoration: 'none' }} className={hoverClass}>{t('footer.privacyPolicy')}</Link>
            <Link to="/impressum" style={{ color: 'inherit', textDecoration: 'none' }} className={hoverClass}>{t('footer.imprint')}</Link>
          </span>
        </div>
      </div>
    </footer>
  );
}
